import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { pollPaymentStatus } from '../utils/payment';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const serviceType = searchParams.get('service');
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid payment session');
      setVerifying(false);
      return;
    }

    async function verifyPayment() {
      try {
        const result = await pollPaymentStatus(sessionId, 15);
        
        if (result.success) {
          setVerified(true);
          setPaymentDetails(result);
        } else if (result.timeout) {
          setError('Payment verification timed out. Please check your email for confirmation.');
        } else if (result.expired) {
          setError('Payment session expired. Please try again.');
        } else {
          setError('Payment could not be verified. Please contact support if you were charged.');
        }
      } catch (err) {
        setError(err.message || 'Failed to verify payment');
      } finally {
        setVerifying(false);
      }
    }

    verifyPayment();
  }, [sessionId]);

  const handleContinue = () => {
    if (serviceType === 'copyright' || serviceType === 'publishing') {
      // Redirect back to editor/dashboard to submit service request
      navigate('/dashboard');
    } else {
      // For conversion services, go to dashboard
      navigate('/dashboard');
    }
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 px-4 py-12">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            {verifying ? (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-spin" />
                <CardTitle>Verifying Payment...</CardTitle>
                <CardDescription>
                  Please wait while we confirm your payment with Stripe.
                </CardDescription>
              </>
            ) : verified ? (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                <CardTitle className="text-green-900">Payment Successful!</CardTitle>
                <CardDescription>
                  Your payment has been confirmed.
                </CardDescription>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
                <CardTitle className="text-red-900">Payment Verification Issue</CardTitle>
                <CardDescription>
                  There was a problem verifying your payment.
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {error && (
              <Alert className="bg-red-50 border-red-300">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {verified && paymentDetails && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Service:</span>
                  <span className="font-semibold">{paymentDetails.package_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Amount Paid:</span>
                  <span className="font-semibold text-green-700">
                    ${paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Status:</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Paid
                  </span>
                </div>
              </div>
            )}

            {verified && (
              <Alert className="bg-blue-50 border-blue-300">
                <AlertDescription className="text-blue-800">
                  ✅ <strong>Next Step:</strong> You can now submit your service request. Your payment has been recorded and you won't be charged again.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              {verified ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1"
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    onClick={handleContinue}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Continue to Service Request
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                  variant="outline"
                >
                  Return to Dashboard
                </Button>
              )}
            </div>

            <p className="text-xs text-center text-gray-500 pt-4">
              A confirmation email has been sent to your registered email address.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
